import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

function normalizeTraceResult(traceResult) {
  if (!traceResult || typeof traceResult !== "object") {
    return traceResult;
  }

  const normalizedInventory = Array.isArray(traceResult.inventory)
    ? traceResult.inventory
    : traceResult.inventory
      ? [traceResult.inventory]
      : [];

  return {
    ...traceResult,
    inventory: normalizedInventory,
  };
}

export function TraceabilityManufacturedViewModel(logout) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [packagingByLotResult, setPackagingByLotResult] = useState(null);
  const [manufacturedTraceResult, setManufacturedTraceResult] = useState(null);
  const [manufacturedLotNumber, setManufacturedLotNumber] = useState("");
  const [selectedProductionId, setSelectedProductionId] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const onTracePackagingByLot = async function (lotNumber) {
    const trimmed = (lotNumber || "").trim();
    if (!trimmed) return;
    setLoading(true);
    setPackagingByLotResult(null);
    setManufacturedTraceResult(null);
    setSelectedProductionId(null);
    const callResult = await makePostApiCall(`traceManufacturedByLot`, {
      lot_number: trimmed,
    });
    if (callResult.success) {
      setPackagingByLotResult(callResult.data);
      setManufacturedLotNumber(trimmed);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message || "No trace found.");
    }
    setLoading(false);
  };

  const onTracePackagingByLotAndPlu = async function (production_id) {
    if (production_id == null || production_id === "") return;
    setLoading(true);
    setSelectedProductionId(production_id);
    const callResult = await makePostApiCall(`traceManufacturedByLotAndPlu`, {
      production_id,
    });
    if (callResult.success) {
      setManufacturedTraceResult(normalizeTraceResult(callResult.data));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data?.message || "No trace found.");
    }
    setLoading(false);
  };

  const onSearchLotNumbers = async function (search) {
    const query = (search || "").trim();
    if (query.length < 3) return [];

    const callResult = await makeGetApiCall(`onSearchLotNumbers/${query}`);
    if (callResult.success) {
      return callResult.data;
    }

    if (callResult.errorStatus === 401) {
      logout();
    }
    return [];
  };

  const reassignPackagingsById = async function (
    manufactured_product_id,
    carton_ids,
  ) {
    const payload = {
      manufactured_product_id,
      carton_ids,
    };
    const callResult = await makePostApiCall("reassignPackagingsById", payload);

    if (!callResult.success && callResult.errorStatus === 401) {
      logout();
    }

    return callResult;
  };

  const onPrintTraceManufacturedByLotAndPluReport = async function () {
    if (selectedProductionId == null || selectedProductionId === "") return;
    setReportLoading(true);
    const callResult = await makePostApiCall(
      `/qaReports/traceManufacturedByLotAndPluReport`,
      {
        production_id: selectedProductionId,
      },
    );
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(
        "Report error",
        callResult.data?.message || "Failed to generate report.",
      );
    }
    setReportLoading(false);
  };

  const clearManufacturedTrace = function () {
    setPackagingByLotResult(null);
    setManufacturedTraceResult(null);
    setManufacturedLotNumber("");
    setSelectedProductionId(null);
  };

  return {
    loading,
    packagingByLotResult,
    manufacturedTraceResult,
    manufacturedLotNumber,
    clearManufacturedTrace,
    slideOver,
    setSlideOver,
    onTracePackagingByLot,
    onTracePackagingByLotAndPlu,
    onSearchLotNumbers,
    reassignPackagingsById,
    selectedProductionId,
    reportLoading,
    onPrintTraceManufacturedByLotAndPluReport,
    emptyModal,
    setEmptyModelOpen,
  };
}
